import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import {
  ANALYTICS_EVENTS,
  type UserSignedUpProperties,
  type UserLoggedInProperties,
  type PracticeSessionStartedProperties,
  type PracticeSessionCompletedProperties,
  type PracticeSessionAbandonedProperties,
  type QuestionAnsweredProperties,
  type QuestionFlaggedProperties,
  type NotesViewedProperties,
  type NotesSectionViewedProperties,
  type SubjectSelectedProperties,
  type PracticeSetupStartedProperties,
  type PracticeFiltersAppliedProperties,
  type UserProperties,
} from "@/lib/analytics/events";

/**
 * Custom hook for PostHog analytics tracking.
 * Provides type-safe event tracking methods.
 */
export function useAnalytics() {
  const posthog = usePostHog();

  // ============================================
  // USER IDENTIFICATION
  // ============================================

  const identifyUser = useCallback(
    (userId: string, properties?: UserProperties) => {
      posthog?.identify(userId, properties);
    },
    [posthog]
  );

  const resetUser = useCallback(() => {
    posthog?.reset();
  }, [posthog]);

  const setUserProperties = useCallback(
    (properties: UserProperties) => {
      posthog?.people?.set(properties);
    },
    [posthog]
  );

  // ============================================
  // AUTHENTICATION EVENTS
  // ============================================

  const trackUserSignedUp = useCallback(
    (properties: UserSignedUpProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.USER_SIGNED_UP, properties);
    },
    [posthog]
  );

  const trackUserLoggedIn = useCallback(
    (properties: UserLoggedInProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.USER_LOGGED_IN, properties);
    },
    [posthog]
  );

  const trackUserLoggedOut = useCallback(() => {
    posthog?.capture(ANALYTICS_EVENTS.USER_LOGGED_OUT);
  }, [posthog]);

  // ============================================
  // PRACTICE EVENTS
  // ============================================

  const trackPracticeSessionStarted = useCallback(
    (properties: PracticeSessionStartedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.PRACTICE_SESSION_STARTED, properties);
    },
    [posthog]
  );

  const trackPracticeSessionCompleted = useCallback(
    (properties: PracticeSessionCompletedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.PRACTICE_SESSION_COMPLETED, properties);
    },
    [posthog]
  );

  const trackPracticeSessionAbandoned = useCallback(
    (properties: PracticeSessionAbandonedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.PRACTICE_SESSION_ABANDONED, properties);
    },
    [posthog]
  );

  const trackQuestionAnswered = useCallback(
    (properties: QuestionAnsweredProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.QUESTION_ANSWERED, properties);
    },
    [posthog]
  );

  const trackQuestionFlagged = useCallback(
    (properties: QuestionFlaggedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.QUESTION_FLAGGED, properties);
    },
    [posthog]
  );

  const trackQuestionUnflagged = useCallback(
    (properties: QuestionFlaggedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.QUESTION_UNFLAGGED, properties);
    },
    [posthog]
  );

  // ============================================
  // NOTES EVENTS
  // ============================================

  const trackNotesViewed = useCallback(
    (properties: NotesViewedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.NOTES_VIEWED, properties);
    },
    [posthog]
  );

  const trackNotesSectionViewed = useCallback(
    (properties: NotesSectionViewedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.NOTES_SECTION_VIEWED, properties);
    },
    [posthog]
  );

  // ============================================
  // STUDY/NAVIGATION EVENTS
  // ============================================

  const trackSubjectSelected = useCallback(
    (properties: SubjectSelectedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.SUBJECT_SELECTED, properties);
    },
    [posthog]
  );

  const trackPracticeSetupStarted = useCallback(
    (properties: PracticeSetupStartedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.PRACTICE_SETUP_STARTED, properties);
    },
    [posthog]
  );

  const trackPracticeFiltersApplied = useCallback(
    (properties: PracticeFiltersAppliedProperties) => {
      posthog?.capture(ANALYTICS_EVENTS.PRACTICE_FILTERS_APPLIED, properties);
    },
    [posthog]
  );

  // ============================================
  // GENERIC CAPTURE (for ad-hoc events)
  // ============================================

  const capture = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      posthog?.capture(eventName, properties);
    },
    [posthog]
  );

  return {
    // User identification
    identifyUser,
    resetUser,
    setUserProperties,

    // Auth events
    trackUserSignedUp,
    trackUserLoggedIn,
    trackUserLoggedOut,

    // Practice events
    trackPracticeSessionStarted,
    trackPracticeSessionCompleted,
    trackPracticeSessionAbandoned,
    trackQuestionAnswered,
    trackQuestionFlagged,
    trackQuestionUnflagged,

    // Notes events
    trackNotesViewed,
    trackNotesSectionViewed,

    // Navigation events
    trackSubjectSelected,
    trackPracticeSetupStarted,
    trackPracticeFiltersApplied,

    // Generic capture
    capture,
  };
}
